import React, { useEffect, useRef, useState, MouseEvent } from "react";
import classNames from "classnames";
import { TypewriterText } from "../../common/components/TypewriterText";
import { MatrixHero } from "../../common/components/MatrixHero";
import "../../scss/about_us.scss";
import { useI18n } from "../../i18n/I18nProvider";
import type { TranslationKey } from "../../i18n/translations";

// ==================== HOOKS ====================

function useInView(threshold = 0.15) {
    const ref = useRef<HTMLDivElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setInView(true); if (ref.current) observer.unobserve(ref.current); } },
            { threshold }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [threshold]);
    return { ref, inView };
}

// ==================== DATA ====================

const SCOPE_ITEM_KEYS: TranslationKey[] = [
    "about_scope_1", "about_scope_2", "about_scope_3", "about_scope_4",
    "about_scope_5", "about_scope_6", "about_scope_7", "about_scope_8",
];

const TEAM_MEMBERS: { name: string; roleKey: TranslationKey; email: string }[] = [
    { name: "mgr Artur Wojciechowski", roleKey: "about_team_role_director", email: "awojciechowski[at]wsiz.edu.pl" },
    { name: "mgr Kamil Dembowski", roleKey: "about_team_role_deputy", email: "kdembowski[at]wsiz.edu.pl" },
    { name: "inż. Mateusz Partyka", roleKey: "about_team_role_employee", email: "mapartyka[at]wsiz.edu.pl" },
    { name: "mgr inż. Adrian Lidwin ", roleKey: "about_team_role_employee", email: "alidwin[at]wsiz.edu.pl" },
    { name: "mgr Justyna Witas ", roleKey: "about_team_role_employee", email: "jwitas[at]wsiz.edu.pl" },
];

// ==================== COMPONENT ====================

const AboutUs: React.FC = () => {
    const [ready, setReady] = useState(false);
    const { t } = useI18n();
    const { ref: refGenesis, inView: inViewGenesis } = useInView(0.15);
    const { ref: refMission, inView: inViewMission } = useInView(0.15);
    const { ref: refScope, inView: inViewScope } = useInView(0.1);
    const { ref: refBeyond, inView: inViewBeyond } = useInView(0.15);
    const { ref: refTeam, inView: inViewTeam } = useInView(0.1);

    useEffect(() => { requestAnimationFrame(() => setReady(true)); }, []);

    const handleMouseMoveGlobal = (e: MouseEvent<HTMLElement>) => {
        const target = e.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        target.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
        target.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
    };

    return (
        <section className={classNames("about-page", { "about-page--ready": ready })} onMouseMove={handleMouseMoveGlobal} aria-label={t("about_section_label")}>
            <div className="grid-overlay" aria-hidden="true" />
            <div className="grid-glow" aria-hidden="true" />

            {/* ===== HERO (reusable) ===== */}
            <MatrixHero
                title={t("about_hero_title")}
                subtitle={t("about_hero_subtitle")}
                density={0.5}
            />

            <div className="about-page__inner">

                {/* ===== GENEZA + MISJA ===== */}
                <div className="content-grid content-grid--two">
                    <article ref={refGenesis} className={classNames("glass-card", { "glass-card--visible": inViewGenesis })}>
                        <div className="glass-card__glow" aria-hidden="true" />
                        <h2 className="glass-card__title">{t("about_genesis_title")}</h2>
                        <p className="glass-card__text">
                            <TypewriterText
                                text={t("about_genesis_text1")}
                                active={inViewGenesis} speed={6}
                            />
                        </p>
                        <p className="glass-card__text">
                            <TypewriterText
                                text={t("about_genesis_text2")}
                                active={inViewGenesis} speed={6}
                            />
                        </p>
                    </article>

                    <article ref={refMission} className={classNames("glass-card", { "glass-card--visible": inViewMission })}>
                        <div className="glass-card__glow" aria-hidden="true" />
                        <h2 className="glass-card__title">{t("about_mission_title")}</h2>
                        <div className="glass-card__highlight">
                            <TypewriterText
                                text={t("about_mission_highlight")}
                                active={inViewMission} speed={6}
                            />
                        </div>
                        <p className="glass-card__text">
                            <TypewriterText
                                text={t("about_mission_text")}
                                active={inViewMission} speed={6}
                            />
                        </p>
                    </article>
                </div>

                {/* ===== ZAKRES DZIAŁALNOŚCI ===== */}
                <article ref={refScope} className={classNames("glass-card glass-card--full", { "glass-card--visible": inViewScope })}>
                    <div className="glass-card__glow" aria-hidden="true" />
                    <h2 className="glass-card__title">{t("about_scope_title")}</h2>
                    <ul className="scope-grid">
                        {SCOPE_ITEM_KEYS.map((key, i) => (
                            <li key={i} className="scope-item" style={{ animationDelay: `${i * 0.08}s` }}>
                                <p className="scope-item__text">
                                    <TypewriterText text={t(key)} active={inViewScope} speed={15} />
                                </p>
                            </li>
                        ))}
                    </ul>
                </article>

                {/* ===== NASZ ZESPÓŁ ===== */}
                <div ref={refTeam} className={classNames("team-section", { "team-section--visible": inViewTeam })}>
                    <h2 className="team-section__title">{t("about_team_title")}</h2>

                    <div className="team-grid">
                        {TEAM_MEMBERS.map((member, idx) => (
                            <article key={idx} className="team-card" style={{ animationDelay: `${idx * 0.1}s` }}>
                                <h3 className="team-card__name">{member.name}</h3>
                                {member.roleKey && <span className="team-card__role">{t(member.roleKey)}</span>}
                                <span className="team-card__contact-label">{t("about_team_contact_label")}</span>
                                <a
                                    href={`mailto:${member.email}`}
                                    className="team-card__email"
                                    aria-label={`${t("about_team_email_label")} ${member.name}`}
                                >
                                    <span className="material-symbols-outlined" aria-hidden="true">{member.email}</span>
                                    
                                </a>
                            </article>
                        ))}
                    </div>
                </div>

                {/* ===== PONAD EDUKACJĘ ===== */}
                <article ref={refBeyond} className={classNames("glass-card glass-card--full glass-card--accent", { "glass-card--visible": inViewBeyond })}>
                    <div className="glass-card__glow" aria-hidden="true" />
                    <h2 className="glass-card__title">{t("about_beyond_title")}</h2>
                    <p className="glass-card__text">
                        <TypewriterText
                            text={t("about_beyond_text1")}
                            active={inViewBeyond} speed={2}
                        />
                    </p>
                    <p className="glass-card__text">
                        <TypewriterText
                            text={t("about_beyond_text2")}
                            active={inViewBeyond} speed={2}
                        />
                    </p>
                </article>

            </div>
        </section>
    );
};

export default AboutUs;